'use client'

import React from 'react'
import { ArticlePageFragment } from '@contentsite/src/graphql/generated/graphql-types'
import Link from 'next/link'
import { ArticleCardList } from '@dustin-web/content-components/src/article-card-list'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import Image from '@dustin-web/microsite-components/src/image'
import { cloudflareImageLoader } from '@dustin-web/microsite-components/src/image/cloudflare-loader'
import { Breadcrumbs } from '@dustin-web/microsite-components/src/breadcrumbs'
import { ShareThis } from '../utils/share-this'
import { useTranslations } from '@dustin-web/microsite-components/src/hooks/use-translations'
import { generateFullTag } from '@dustin-web/content-components/src/utils/generate-full-tag'

const GuidePageSchema = dynamic(() =>
  import('../schemas/article-guide-page-schema').then(mod => mod.GuidePageSchema)
)
const ArticlePageSchema = dynamic(() =>
  import('../schemas/article-guide-page-schema').then(mod => mod.ArticlePageSchema)
)
const BlockList = dynamic(() => import('../blocks').then(mod => mod.BlockList))
const MacroContent = dynamic(() => import('../macros/macro-content').then(mod => mod.MacroContent))

const Article = (props: { page: ArticlePageFragment }) => {
  const { page } = props
  const { translate } = useTranslations()

  const mainImageUrl = page.mainImage?.url
  const fullMainImageUrl =
    mainImageUrl && (mainImageUrl?.includes('http') ? mainImageUrl : `https:${mainImageUrl}`)

  const breadcrumbs = page.breadcrumbs.map(breadcrumb => ({
    displayName: breadcrumb.displayName,
    url: breadcrumb.url,
    clientNavigationRoute: breadcrumb.url,
  }))

  const isGuidePage = page.tags.some(tag => tag.toLowerCase() === 'guide')
  const fromatedArticlesList = page.categoryArticles.map(article => {
    return {
      ...article,
      tags: generateFullTag(article.tags, page.blogUrl, article.categoryName),
    }
  })
  return (
    <div className="u-bg-white">
      {!page.ogImage && fullMainImageUrl && (
        // If set, ogImage overrides default image
        <Head>
          <meta property="og:image" content={fullMainImageUrl} />
        </Head>
      )}
      {isGuidePage ? <GuidePageSchema page={page} /> : <ArticlePageSchema page={page} />}
      <div className="container">
        <Breadcrumbs path={breadcrumbs} />
      </div>
      {page.heroImage && (
        <div
          className="u-mb-32"
          style={{
            backgroundImage: `url(${cloudflareImageLoader({
              src: page.heroImage.relativeUrl,
              width: 1920,
            })})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            height: '300px',
          }}></div>
      )}

      <div className="container">
        {!page.heroImage && page.mainImage && (
          <Image src={page.mainImage.relativeUrl} alt="" width={640} />
        )}
        {page.youTubeId && (
          <div className="embed-responsive embed-responsive-16by9">
            <iframe
              className="embed-responsive-item "
              src={`https://www.youtube.com/embed/${page.youTubeId}`}
              allowFullScreen></iframe>
          </div>
        )}

        <div className="row">
          <div className="col-md-8">
            <p className="u-color-mute u-fs-20 u-mb-4">{page.categoryName}</p>
            <h1 className="h1-sm">{page.header}</h1>
            {page.excerpt && (
              <blockquote>
                <p>{page.excerpt}</p>
              </blockquote>
            )}
          </div>
        </div>

        <article className="c-article-body u-mb-32">
          {page.contentBlocks?.length > 0 ? (
            <BlockList blocks={page.contentBlocks} />
          ) : (
            <MacroContent content={page.body} />
          )}
        </article>
        <div className="u-pt-16 u-pb-32">
          <div className="u-mb-32">
            <p className="u-mb-32">
              {translate('LastModified')}: {page.updatedDateText}
            </p>

            <h2 className="h4 u-mb-8">{translate('tags')}</h2>
            <div className="c-pipe-separator">
              {page.tags.map(tag => {
                return (
                  <Link
                    key={tag}
                    href={`${page.blogUrl}?category=${encodeURIComponent(
                      page.categoryName
                    )}&tag=${encodeURIComponent(tag)}`}>
                    {tag}
                  </Link>
                )
              })}
            </div>
          </div>
          <div className="u-mb-32">
            <ShareThis />
          </div>
        </div>
      </div>
      {page.categoryArticles?.length > 0 && (
        <div className="c-box c-box--section u-bg-gray-02 u-pt-64 u-pb-96">
          <div className="container" id="morearticles">
            <h2 className="u-mb-24 u-fs-20">
              {translate('Article_MoreArticlesInCategory')}{' '}
              <span>
                <Link href={`${page.blogUrl}?category=${encodeURIComponent(page.categoryName)}`}>
                  {page.categoryName}
                </Link>
              </span>
            </h2>
            <ArticleCardList
              articlesList={fromatedArticlesList}
              hideCategory
              maxNumberOfArticles={3}
              listCategory={page.categoryName}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Article
